/**
 * Conteúdo interno do modal para envio ao prontuário (sem Dialog próprio).
 * Lista as 8 métricas do dashboard; sem valor = checkbox desmarcado e desabilitado.
 */

import { Add } from "@mui/icons-material";
import {
  Alert,
  Button,
  Checkbox,
  FormControlLabel,
  FormGroup,
  Link,
  Stack,
  Typography,
} from "@mui/material";
import dayjs from "dayjs";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";

import { useConsentContext } from "@/contexts/ConsentContext";
import { useOrganizationContext } from "@/contexts/OrganizationContext";
import { usePrivacyContext } from "@/contexts/PrivacyContext";
import { useSessionContext } from "@/contexts/SessionContext";
import { GetConsents } from "@/services/consent";
import { PostWearableObservationResource } from "@/services/fhir/observation/PostWearableObservation";
import {
  WEARABLE_LOINC_LIST,
  buildWearableObservationsForPeriod,
  hasWearableValueForKey,
  hasWearableValueForKeyInPeriod,
  type WearableShareKey,
} from "@/services/fhir/observation/wearableObservationBuilder";
import { useDatetime } from "@/utils/useDatetime";
import { useFormatter } from "@/utils/useFormatter";
import type { IGoogleHealthFitnessBundle } from "@/services/google/health";
import type { FitnessRangeDays } from "@/pages/medicalcare/components/Wearable/WearableDateRangeChips";
import { formatWearablePeriodLabel } from "@/pages/medicalcare/components/Wearable/wearableMetricAggregation";
import { formatarDadosGoogleHealth } from "@/utils/wearableFitFormat";
import { ConsentTabs } from "@/pages/consents/components/ConsentTabs";
import { ConfirmConsentDialog } from "@/pages/consents/components/ConfirmConsentDialog";
import { RenewConsentDialog } from "@/pages/consents/components/RenewConsentDialog";
import { RevokeConsentDialog } from "@/pages/consents/components/RevokeConsentDialog";

import type { Dayjs } from "dayjs";

interface FitnessDataSharePanelProps {
  fitnessBundle: IGoogleHealthFitnessBundle | null;
  selectedDate: Dayjs;
  periodStart: Dayjs;
  periodEnd: Dayjs;
  rangeDays: FitnessRangeDays;
  /** Volta à tela de cards / busca. */
  onBack: () => void;
  /** Fecha o modal de fitness ao sair para outra rota (ex.: /consent). */
  onCloseModal?: () => void;
  /** Seleção controlada pelo pai (cards do dashboard mobile). */
  selectedKeys?: Record<string, boolean>;
  onSelectedKeysChange?: (keys: Record<string, boolean>) => void;
  /** Layout enxuto para seção embutida / telas estreitas. */
  compact?: boolean;
  embedded?: boolean;
}

export function FitnessDataSharePanel({
  fitnessBundle,
  selectedDate,
  periodStart,
  periodEnd,
  rangeDays,
  onBack,
  onCloseModal,
  selectedKeys: controlledSelectedKeys,
  onSelectedKeysChange,
  compact = false,
  embedded = false,
}: FitnessDataSharePanelProps) {
  const navigate = useNavigate();
  const { user } = useSessionContext();
  const { isSharing } = usePrivacyContext();
  const { organizationList } = useOrganizationContext();
  const {
    confirmConsentDialog,
    revokeDialog,
    renewDialog,
    setConsentType,
  } = useConsentContext();

  const { formatNameDisplay } = useFormatter;
  const { formatToDDMMYYYY, formatToHHmm } = useDatetime;

  const [searchField] = useState("");
  const [granted, setGranted] = useState<any>([]);
  const [received] = useState([]);
  const [refetch, setRefetch] = useState(false);
  const [selectedTab, setSelectedTab] = useState("1");
  const [sending, setSending] = useState(false);

  const fit = useMemo(
    () => formatarDadosGoogleHealth(fitnessBundle, selectedDate),
    [fitnessBundle, selectedDate]
  );

  const [internalSelectedKeys, setInternalSelectedKeys] = useState<Record<string, boolean>>({});
  const isControlledSelection = controlledSelectedKeys !== undefined;
  const selectedKeys = isControlledSelection ? controlledSelectedKeys : internalSelectedKeys;

  const setSelectedKeys = (
    updater: Record<string, boolean> | ((prev: Record<string, boolean>) => Record<string, boolean>)
  ) => {
    if (typeof updater === "function") {
      const next = updater(selectedKeys);
      if (onSelectedKeysChange) onSelectedKeysChange(next);
      else setInternalSelectedKeys(next);
      return;
    }
    if (onSelectedKeysChange) onSelectedKeysChange(updater);
    else setInternalSelectedKeys(updater);
  };

  // Ao mudar os dados, marca só o que tem valor; sem valor fica false e desabilitado na UI.
  useEffect(() => {
    if (isControlledSelection) return;
    const initial: Record<string, boolean> = {};
    for (const def of WEARABLE_LOINC_LIST) {
      initial[def.key] =
        rangeDays === 1
          ? hasWearableValueForKey(fit, def.key)
          : hasWearableValueForKeyInPeriod(
              fitnessBundle,
              periodStart,
              periodEnd,
              def.key
            );
    }
    setInternalSelectedKeys(initial);
    setSelectedTab("1");
  }, [fit, isControlledSelection, fitnessBundle, periodStart, periodEnd, rangeDays]);

  // Não chamar resetStates() aqui: a função do contexto não é estável (novo identity a cada
  // render do provider), então um efeito com [resetStates] reexecutava e fechava Renovar/Revogar
  // logo após abrir.

  const formatConsent = useCallback(
    (identifier: any, name: any, el: any) => {
      const isValid =
        dayjs(el?.vigencia?.split("T")?.[0]).isAfter(dayjs()) || !el?.vigencia;

      return {
        identifier: identifier || "-",
        name: formatNameDisplay(name || identifier) || "-",
        date: dayjs(el?.hora).isValid() ? formatToDDMMYYYY(dayjs(el.hora)) : "-",
        hour: dayjs(el?.hora).isValid() ? formatToHHmm(dayjs(el.hora)) : "-",
        vigency: dayjs(el?.vigencia).isValid()
          ? formatToDDMMYYYY(dayjs(el.vigencia))
          : null,
        isValid,
        motivo: el?.contexto?.motivoAcesso,
        status: isValid ? "valid" : "expired",
      };
    },
    [formatNameDisplay, formatToDDMMYYYY, formatToHHmm]
  );

  useEffect(() => {
    if (!isSharing) return;

    async function fetchData() {
      try {
        const grantedResponse = await GetConsents({
          identifier: user.username,
          type: "granted",
        });

        const activeConsents = grantedResponse?.data?.filter(
          (el: any) => el?.acao === "Autorizar"
        );

        const auxIndividual =
          activeConsents
            ?.filter(
              (el: any) =>
                !el.contexto.instituicao &&
                el?.sujeito?.id !== user.username &&
                el?.sujeito?.nome
            )
            ?.map((el: any) =>
              formatConsent(el?.sujeito?.id, el?.sujeito?.nome, el)
            ) || [];

        const auxOrganization =
          activeConsents
            ?.filter((el: any) => el.contexto.instituicao)
            ?.map((el: any) => {
              const organization = organizationList?.find(
                (org: any) =>
                  org.resource?.id.split("-")[1] === el?.contexto?.instituicao
              );
              const alias =
                organization?.resource?.alias?.[0] !== "null"
                  ? organization?.resource?.alias?.[0]
                  : organization?.resource?.name;
              return formatConsent(el?.contexto?.instituicao, alias, el);
            }) || [];

        setGranted([auxIndividual, auxOrganization]);
      } catch (err) {
        console.error("Fetch consents (wearable share)", err);
      }
    }

    fetchData();
  }, [isSharing, user.username, refetch, organizationList, formatConsent]);

  const handleChangeTab = (_: React.SyntheticEvent, newValue: string) => {
    setSelectedTab(newValue);
    if (newValue === "1") setConsentType("cpf");
    else if (newValue === "2") setConsentType("cnpj");
  };

  const selectableCount = useMemo(() => {
    return WEARABLE_LOINC_LIST.filter((d) => {
      const hasValue =
        rangeDays === 1
          ? hasWearableValueForKey(fit, d.key)
          : hasWearableValueForKeyInPeriod(
              fitnessBundle,
              periodStart,
              periodEnd,
              d.key
            );
      return hasValue && selectedKeys[d.key];
    }).length;
  }, [fit, selectedKeys, fitnessBundle, periodStart, periodEnd, rangeDays]);

  const handleSubmit = async () => {
    const keys = (Object.keys(selectedKeys) as WearableShareKey[]).filter((k) => {
      const hasValue =
        rangeDays === 1
          ? hasWearableValueForKey(fit, k)
          : hasWearableValueForKeyInPeriod(
              fitnessBundle,
              periodStart,
              periodEnd,
              k
            );
      return selectedKeys[k] && hasValue;
    });
    if (!keys.length) {
      toast.error("Selecione ao menos um dado com valor para enviar.");
      return;
    }

    if (!fitnessBundle) {
      toast.error("Nenhum dado fitness disponível para enviar.");
      return;
    }

    const observations = buildWearableObservationsForPeriod({
      fitnessBundle,
      periodStart: rangeDays === 1 ? selectedDate : periodStart,
      periodEnd: rangeDays === 1 ? selectedDate : periodEnd,
      patientId: user.username,
      patientDisplay: user.name || user.username,
      keys,
    });

    if (!observations.length) {
      toast.error("Nenhuma observação válida para enviar.");
      return;
    }

    setSending(true);
    try {
      for (const obs of observations) {
        await PostWearableObservationResource(obs);
      }
      toast.success(
        `${observations.length} observação(ões) enviada(s) com sucesso.`
      );
      onBack();
    } catch {
      /* toast já em PostWearableObservation */
    } finally {
      setSending(false);
    }
  };

  return (
    <>
      <Stack spacing={compact ? 1 : 1.25} sx={{ pt: embedded ? 0 : 0.5 }}>
        {!isSharing ? (
          <Typography variant="body2" color="text.secondary" sx={{ fontSize: compact ? "0.75rem" : undefined }}>
            Para registrar observações no prontuário, é necessário permitir
            compartilhamento nas configurações de privacidade.{" "}
            <Link
              component="button"
              type="button"
              variant="body2"
              onClick={() => navigate("/privacy")}
              sx={{ verticalAlign: "baseline" }}
            >
              Abrir privacidade
            </Link>
          </Typography>
        ) : (
          <>
            <Stack spacing={compact ? 0.75 : 1}>
              <Typography
                variant={compact ? "subtitle2" : "subtitle1"}
                fontWeight={700}
                color="primary800.main"
              >
                Os dados serão enviados para seu médico em atendimento 
              </Typography>
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ lineHeight: 1.45, fontSize: compact ? "0.72rem" : undefined }}
              >
                As <strong>pessoas</strong> e as <strong>unidades de saúde</strong> com
                compartilhamento <strong>concedido</strong> por você (listados nas abas
                abaixo) poderão <strong>visualizar no prontuário</strong> as informações
                de saúde que você registrar ao confirmar o envio.
              </Typography>

              <Button
                variant="contained"
                color="primary"
                size={compact ? "small" : "medium"}
                startIcon={<Add />}
                onClick={() => {
                  navigate("/consent");
                  onCloseModal?.();
                }}
                sx={{
                  alignSelf: embedded ? "stretch" : "flex-start",
                  width: embedded ? "100%" : undefined,
                  textTransform: "none",
                  fontSize: compact ? "0.72rem" : undefined,
                }}
              >
                Novo compartilhamento
              </Button>

              <ConsentTabs
                embedGrantedOnly
                selectedTab={selectedTab}
                handleChangeTab={handleChangeTab}
                searchField={searchField}
                granted={granted}
                received={received}
              />
            </Stack>

            <Typography
              variant={compact ? "subtitle2" : "subtitle1"}
              fontWeight={700}
              color="primary800.main"
              sx={{ mt: compact ? 0.5 : 1 }}
            >
              O que deseja enviar
            </Typography>
            <Alert
              severity="info"
              variant="outlined"
              sx={{
                py: 0.25,
                alignItems: "center",
                "& .MuiAlert-message": {
                  py: 0.25,
                  fontSize: compact ? "0.72rem" : "0.8125rem",
                  lineHeight: 1.4,
                },
              }}
            >
              Intervalo selecionado:{" "}
              <strong>
                {formatWearablePeriodLabel(periodStart, periodEnd, rangeDays)}
              </strong>
              . {rangeDays > 1
                ? "Cada dia com dados será enviado ao prontuário como um registro separado. Dias sem valores não gerarão registros."
                : "As medições serão registradas nesta data."}
            </Alert>

            <FormGroup
              sx={{
                mt: 0.25,
                gap: 0,
                maxHeight: embedded ? 180 : undefined,
                overflowY: embedded ? "auto" : undefined,
                pr: embedded ? 0.25 : 0,
                "& .MuiFormControlLabel-root": {
                  margin: 0,
                  minHeight: 0,
                  py: compact ? 0.05 : 0.125,
                },
              }}
            >
              {WEARABLE_LOINC_LIST.map((def) => {
                const hasValue =
                  rangeDays === 1
                    ? hasWearableValueForKey(fit, def.key)
                    : hasWearableValueForKeyInPeriod(
                        fitnessBundle,
                        periodStart,
                        periodEnd,
                        def.key
                      );
                return (
                  <FormControlLabel
                    key={def.key}
                    sx={{
                      alignItems: "center",
                      opacity: hasValue ? 1 : 0.55,
                      m: 0,
                      py: 0,
                    }}
                    control={
                      <Checkbox
                        size="small"
                        checked={hasValue && Boolean(selectedKeys[def.key])}
                        disabled={!hasValue}
                        onChange={(e) => {
                          if (!hasValue) return;
                          setSelectedKeys((prev) => ({
                            ...prev,
                            [def.key]: e.target.checked,
                          }));
                        }}
                        sx={{ py: 0 }}
                      />
                    }
                    label={
                      <Typography
                        variant="body2"
                        fontWeight={600}
                        sx={{ lineHeight: 1.2, fontSize: compact ? "0.72rem" : undefined }}
                      >
                        {def.text}
                      </Typography>
                    }
                  />
                );
              })}
            </FormGroup>

            <Stack
              direction={embedded ? "column" : "row"}
              justifyContent="flex-end"
              spacing={embedded ? 0.75 : 0}
              sx={{ pt: 1 }}
            >
              {!embedded && (
                <Button
                  variant="outlined"
                  onClick={onBack}
                  sx={{ textTransform: "none", mr: embedded ? 0 : 1 }}
                >
                  Voltar
                </Button>
              )}
              <Button
                variant="contained"
                fullWidth={embedded}
                size={compact ? "small" : "medium"}
                onClick={handleSubmit}
                disabled={sending || selectableCount === 0}
                sx={{ textTransform: "none", fontWeight: 700, fontSize: compact ? "0.78rem" : undefined }}
              >
                {sending ? "Enviando..." : "Enviar selecionados"}
              </Button>
            </Stack>
          </>
        )}
      </Stack>

      {confirmConsentDialog && (
        <ConfirmConsentDialog setRefetch={setRefetch} />
      )}
      {revokeDialog && <RevokeConsentDialog setRefetch={setRefetch} />}
      {renewDialog && <RenewConsentDialog setRefetch={setRefetch} />}
    </>
  );
}
